import { addUrlChangeListener, getNamespaceData, navigateTo, resolve, URLInitOptions } from '@seamless/router';
import { adjustPDPUrl } from '../utils/aem.util';
import AbstractRouter from './abstract-router';
import { Route, RouteNameOrLocation, SeamlessRouteConfig, SeamlessRouterOptions } from './types';

export default class SeamlessRouterImpl extends AbstractRouter {
  namespace: string;
  options: SeamlessRouterOptions;

  constructor(options: SeamlessRouterOptions, namespace = 'emh') {
    super();
    this.options = options;
    this.namespace = namespace;
    this.checkIfRedirectIsNecessary();
  }

  push(routeLocation: RouteNameOrLocation, onComplete?: () => void) {
    this.navigateTo(routeLocation, false, onComplete);
  }

  replace(routeLocation: RouteNameOrLocation, onComplete?: () => void) {
    this.navigateTo(routeLocation, true, onComplete);
  }

  private navigateTo(routeLocation: RouteNameOrLocation, replace: boolean, onComplete?: () => void) {
    const urlOpts: URLInitOptions = { namespace: this.namespace };
    const globalOpts: URLInitOptions = { namespace: '' };
    let base: string | undefined;
    let url: URL | null;

    if (typeof routeLocation === 'string') {
      const route = this.findRouteConfig(routeLocation) as SeamlessRouteConfig;

      if (route) {
        if (route.redirect) {
          this.replace(route.redirect);

          return;
        }

        base = route.link;
        urlOpts.pathname = route.path;
      } else if (routeLocation.startsWith('/')) {
        urlOpts.pathname = routeLocation;
      }
    } else {
      if (routeLocation.name) {
        const route = this.findRouteConfig(routeLocation.name) as SeamlessRouteConfig;

        base = route?.link;
        urlOpts.pathname = route?.path;
      } else if (routeLocation.path) {
        urlOpts.pathname = routeLocation.path;
      }

      urlOpts.searchParams = routeLocation.query || routeLocation.params;

      if (routeLocation.globalQuery) {
        globalOpts.searchParams = routeLocation.globalQuery;
      }
    }

    // Support absolut links
    if (base?.startsWith('http')) {
      url = new URL(base);

      // Add all search params to it if they are provided
      Object.entries(urlOpts.searchParams || {}).forEach(([key, value]) => {
        url?.searchParams.set(this.namespace + key, value || '');
      });
    } else {
      url = resolve([urlOpts, globalOpts], { base });
    }

    // In case of a navigation to PDP a URL should be adjusted to support pathname props
    if (url && typeof routeLocation === 'object' && routeLocation.name === 'pdp') {
      url = adjustPDPUrl(url, this.namespace);
    }

    if (url) {
      if (url.host !== window.location.host) {
        window.location.href = url.toString();
      } else {
        navigateTo(url, replace);
      }

      if (onComplete) {
        onComplete();
      }
    }
  }

  getCurrentRoute(): Route {
    const urlData = getNamespaceData(this.namespace);
    const routeConfig = this.findRouteConfig(urlData.subPathname);

    return {
      query: this.handleArrayLikeSearchParams(urlData.searchParams),
      params: urlData.searchParams || {},
      path: urlData.subPathname,
      name: routeConfig?.name?.toString(),
      meta: routeConfig?.meta,
    };
  }

  get routes() {
    return this.options.routes;
  }

  onRouteChange(cb: (to: Route, from: Route) => void): void {
    addUrlChangeListener(
      ({ to, from }) => {
        const toRouteConfig = this.findRouteConfig(to.subPathname);
        const fromRouteConfig = this.findRouteConfig(from.subPathname);

        const _to: Route = {
          path: to.subPathname,
          query: this.handleArrayLikeSearchParams(to.searchParams),
          params: to.searchParams || {},
          name: toRouteConfig?.name?.toString(),
        };

        const _from: Route = {
          path: from.subPathname,
          query: this.handleArrayLikeSearchParams(from.searchParams),
          params: from.searchParams || {},
          name: fromRouteConfig?.name?.toString(),
        };

        cb(_to, _from);
      },
      { namespace: this.namespace },
    );
  }

  /**
   * Helper method to convert comma separated query param values to arrays to make it consistent
   * with the Vue router which also uses arrays.
   **/
  private handleArrayLikeSearchParams(searchParams?: Record<string, string>): Record<string, string | string[]> {
    if (!searchParams) {
      return {};
    }

    return Object.entries(searchParams).reduce((result, [key, value]) => {
      const isArrayParam = /(?=\S),(?=\S)/.test(value);

      if (isArrayParam) {
        result[key] = value.split(/(?=\S),(?=\S)/);
      } else {
        result[key] = value;
      }

      return result;
    }, {});
  }

  private checkIfRedirectIsNecessary() {
    const route = this.getCurrentRoute();
    const routeConfig = this.findRouteConfig(route.path) as SeamlessRouteConfig;

    if (routeConfig?.redirect) {
      this.replace(routeConfig.redirect);
    }
  }
}
